import { z } from 'zod';
import { MAX_FILE_SIZE_BYTES, safeFileNameSchema } from '../dms/upload.schema';

/**
 * Zeichnungen am Artikel (technische Zeichnungen). Bewusst auf **PDF** beschränkt
 * — eingebettet auf der Artikel-Detailseite (Inline-Vorschau). Die Bytes laufen
 * über denselben Speicher-Port wie das DMS (ADR 0021/0023), hier nur als
 * eigenständige Artikel-Stammdaten (analog zu Preisstaffeln).
 */
export const DRAWING_MIME_TYPE = 'application/pdf';

/** Maximale Größe einer Zeichnung — teilt sich die Obergrenze mit dem DMS. */
export const MAX_DRAWING_SIZE_BYTES = MAX_FILE_SIZE_BYTES;

/** Schritt 1 des Uploads: Metadaten anlegen, Upload-URL anfordern. */
export const drawingUploadInitSchema = z.object({
  title: z.string().trim().min(1).max(200),
  revision: z.string().trim().max(40).optional(),
  // Sicherer Basename (kein Pfadtrenner/`..`/Steuerzeichen) — der Name landet im
  // Speicherpfad (posix.join), sonst Path-Traversal aus dem Dokumenten-Root.
  fileName: safeFileNameSchema,
  mimeType: z.literal(DRAWING_MIME_TYPE, {
    errorMap: () => ({ message: 'Nur PDF-Dateien sind als Zeichnung erlaubt.' }),
  }),
  size: z.number().int().min(0).max(MAX_DRAWING_SIZE_BYTES),
});
export type DrawingUploadInitInput = z.infer<typeof drawingUploadInitSchema>;

export const drawingUploadInitResponseSchema = z.object({
  drawingId: z.string().uuid(),
  uploadUrl: z.string(),
  objectKey: z.string(),
});
export type DrawingUploadInitResponse = z.infer<typeof drawingUploadInitResponseSchema>;

/** Titel/Revision pflegen oder zur Hauptzeichnung machen. */
export const updateDrawingSchema = z
  .object({
    title: z.string().trim().min(1).max(200).optional(),
    revision: z.string().trim().max(40).nullable().optional(),
    isPrimary: z.boolean().optional(),
  })
  .refine((value) => Object.keys(value).length > 0, {
    message: 'Mindestens ein Feld muss angegeben werden.',
  });
export type UpdateDrawingInput = z.infer<typeof updateDrawingSchema>;

export const drawingResponseSchema = z.object({
  id: z.string().uuid(),
  articleId: z.string().uuid(),
  title: z.string(),
  revision: z.string().nullable(),
  fileName: z.string(),
  mimeType: z.string(),
  size: z.number().int(),
  isPrimary: z.boolean(),
  createdAt: z.string().datetime(),
});
export type DrawingResponse = z.infer<typeof drawingResponseSchema>;
